import React, { useState, useEffect } from 'react';
import { useFamilyAuth } from '@/contexts/FamilyAuthContext';
import {
  isPushSupported,
  getPermissionStatus,
  setupPushNotifications,
  onForegroundMessage
} from '@/utils/pushNotifications';

/**
 * Push Notification Settings Component
 * 
 * Ermöglicht Family Users:
 * - Browser-Push zu aktivieren/deaktivieren
 * - Status der Push-Berechtigung zu sehen
 * - Push-Token zu registrieren
 */
export default function PushNotificationSettings() {
  const { familyUser } = useFamilyAuth();
  const [pushStatus, setPushStatus] = useState({
    supported: false,
    permission: 'default',
    enabled: false,
    token: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    checkPushStatus();
    
    // Listen for foreground messages
    const unsubscribe = onForegroundMessage((payload) => {
      console.log('[Push UI] Foreground message received:', payload);
      // Show success message
      setSuccess('Neue Benachrichtigung empfangen!');
      setTimeout(() => setSuccess(null), 3000);
    });
    
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, []);

  const checkPushStatus = () => {
    const supported = isPushSupported();
    const permission = getPermissionStatus();
    const enabled = permission === 'granted' && familyUser?.push_token;
    
    setPushStatus({
      supported,
      permission,
      enabled,
      token: familyUser?.push_token
    });
  };

  const handleEnablePush = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get auth token
      const authToken = localStorage.getItem('familyToken');
      if (!authToken) {
        throw new Error('Nicht angemeldet');
      }
      
      // Setup push notifications
      const token = await setupPushNotifications(authToken);
      
      // Update status
      setPushStatus(prev => ({
        ...prev,
        permission: 'granted',
        enabled: true,
        token
      }));
      
      setSuccess('Push-Benachrichtigungen erfolgreich aktiviert!');
    } catch (err) {
      console.error('[Push UI] Enable error:', err);
      
      // User-friendly error messages
      let errorMsg = 'Fehler beim Aktivieren der Push-Benachrichtigungen';
      
      if (err.message.includes('not supported')) {
        errorMsg = 'Ihr Browser unterstützt keine Push-Benachrichtigungen';
      } else if (err.message.includes('denied')) {
        errorMsg = 'Benachrichtigungen wurden blockiert. Bitte erlauben Sie Benachrichtigungen in Ihren Browser-Einstellungen.';
      } else if (err.message.includes('FCM')) {
        errorMsg = 'Push-Dienst ist nicht konfiguriert. Bitte kontaktieren Sie den Administrator.';
      } else if (err.message.includes('token')) {
        errorMsg = 'Fehler beim Generieren des Push-Tokens. Bitte versuchen Sie es erneut.';
      }
      
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // iOS-spezifische Meldung
  if (pushStatus.supported === 'ios-not-supported') {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 border border-neutral-600/40">
        <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">📱 Push-Benachrichtigungen</h3>
        <div className="bg-[#0EA5E9]/10 border border-[#0EA5E9]/40 rounded-xl p-4">
          <div className="flex gap-3">
            <div className="text-2xl">ℹ️</div>
            <div className="flex-1 space-y-2 text-sm text-[#CBD5F5]">
              <p className="font-bold text-[#F9FAFB]">Hinweis für iOS-Nutzer:</p>
              <p>
                Push-Benachrichtigungen werden auf diesem Gerät derzeit nicht unterstützt. 
                Dies liegt an den Einschränkungen von Apple für Web-Push auf iOS.
              </p>
              <p className="text-neutral-500">
                Sie können die Hayat Family App weiterhin nutzen und werden über wichtige Ereignisse 
                per E-Mail oder SMS informiert.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!pushStatus.supported) {
    return (
      <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 border border-neutral-600/40">
        <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">📱 Push-Benachrichtigungen</h3>
        <div className="bg-[#F6B352]/10 border border-[#F6B352]/40 rounded-xl p-4">
          <p className="text-[#F6B352] font-semibold mb-2">⚠️ Ihr Browser unterstützt keine Push-Benachrichtigungen.</p>
          <p className="text-sm text-neutral-500">
            Bitte verwenden Sie Chrome, Firefox oder Edge für Push-Benachrichtigungen.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900/80 backdrop-blur-xl rounded-xl p-6 shadow-lg border border-neutral-600/40">
      <h3 className="text-xl font-bold text-[#F9FAFB] mb-4">📱 Push-Benachrichtigungen</h3>
      
      <div className="mb-6">
        <p className="text-sm text-[#CBD5F5]">
          Erhalten Sie sofortige Benachrichtigungen bei Notfällen und wichtigen Ereignissen direkt auf Ihrem Gerät.
        </p>
      </div>

      {/* Status Display */}
      <div className="space-y-3 mb-6">
        <div className="flex items-center justify-between py-3 px-4 bg-neutral-800/50 rounded-xl">
          <span className="text-sm font-medium text-neutral-500">Browser-Unterstützung:</span>
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${
            pushStatus.supported 
              ? 'bg-[#22C55E]/20 text-[#22C55E]' 
              : 'bg-[#EF4444]/20 text-[#EF4444]'
          }`}>
            {pushStatus.supported ? '✓ Unterstützt' : '✗ Nicht unterstützt'}
          </span>
        </div>
        
        <div className="flex items-center justify-between py-3 px-4 bg-neutral-800/50 rounded-xl">
          <span className="text-sm font-medium text-neutral-500">Berechtigung:</span>
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${
            pushStatus.permission === 'granted' ? 'bg-[#22C55E]/20 text-[#22C55E]' : 
            pushStatus.permission === 'denied' ? 'bg-[#EF4444]/20 text-[#EF4444]' : 
            'bg-[#F6B352]/20 text-[#F6B352]'
          }`}>
            {pushStatus.permission === 'granted' && '✓ Erteilt'}
            {pushStatus.permission === 'denied' && '✗ Verweigert'}
            {pushStatus.permission === 'default' && '⚠ Noch nicht erteilt'}
            {pushStatus.permission === 'unsupported' && '✗ Nicht unterstützt'}
          </span>
        </div>
        
        <div className="flex items-center justify-between py-3 px-4 bg-neutral-800/50 rounded-xl">
          <span className="text-sm font-medium text-neutral-500">Push-Benachrichtigungen:</span>
          <span className={`text-sm font-bold px-3 py-1 rounded-full ${
            pushStatus.enabled 
              ? 'bg-[#22C55E]/20 text-[#22C55E]' 
              : 'bg-neutral-700 text-neutral-400'
          }`}>
            {pushStatus.enabled ? '✓ Aktiv' : '○ Inaktiv'}
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-[#EF4444]/10 border border-[#EF4444]/40 rounded-xl p-4 mb-6 flex items-start gap-3 animate-fade-in">
          <span className="text-2xl">⚠️</span>
          <span className="text-sm text-[#EF4444] flex-1">{error}</span>
        </div>
      )}

      {/* Success Message */}
      {success && (
        <div className="bg-[#22C55E]/10 border border-[#22C55E]/40 rounded-xl p-4 mb-6 flex items-start gap-3 animate-fade-in">
          <span className="text-2xl">✓</span>
          <span className="text-sm text-[#22C55E] flex-1">{success}</span>
        </div>
      )}

      {/* Action Button */}
      {!pushStatus.enabled && (
        <button
          onClick={handleEnablePush}
          disabled={loading || pushStatus.permission === 'denied'}
          className="w-full py-4 bg-gradient-to-r from-[#5B4B8A] to-[#4a3d72] text-white rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px] flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              Aktiviere...
            </>
          ) : (
            <>
              <span className="text-xl">🔔</span>
              Push-Benachrichtigungen aktivieren
            </>
          )}
        </button>
      )}

      {pushStatus.permission === 'denied' && (
        <div className="mt-6 bg-[#F6B352]/10 border border-[#F6B352]/40 rounded-xl p-4">
          <p className="text-sm text-[#F6B352] mb-3">
            <strong>Benachrichtigungen blockiert:</strong><br/>
            Um Push-Benachrichtigungen zu aktivieren, müssen Sie die Berechtigung in Ihren Browser-Einstellungen erteilen.
          </p>
          <details className="text-xs text-neutral-500">
            <summary className="cursor-pointer font-semibold text-[#CBD5F5] hover:text-[#6DCEE8]">Anleitung anzeigen</summary>
            <ul className="mt-3 space-y-2 pl-4">
              <li><strong className="text-[#F9FAFB]">Chrome:</strong> Einstellungen → Datenschutz & Sicherheit → Website-Einstellungen → Benachrichtigungen</li>
              <li><strong className="text-[#F9FAFB]">Firefox:</strong> Einstellungen → Datenschutz & Sicherheit → Berechtigungen → Benachrichtigungen</li>
              <li><strong className="text-[#F9FAFB]">Safari:</strong> Safari → Einstellungen → Websites → Benachrichtigungen</li>
            </ul>
          </details>
        </div>
      )}

      {pushStatus.enabled && (
        <div className="mt-6 bg-[#22C55E]/10 border border-[#22C55E]/40 rounded-xl p-4">
          <p className="text-sm text-[#22C55E] font-semibold mb-3">
            ✓ Push-Benachrichtigungen sind aktiv. Sie erhalten Benachrichtigungen für:
          </p>
          <ul className="space-y-2 text-sm text-[#CBD5F5]">
            <li className="flex items-center gap-2">
              <span className="text-lg">🚨</span>
              Notfälle
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">📅</span>
              Wichtige Erinnerungen
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">💊</span>
              Medikamenten-Einnahme
            </li>
            <li className="flex items-center gap-2">
              <span className="text-lg">📞</span>
              Verpasste Anrufe
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
